import {
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { BaseLogo } from "../../core/models/entities/BaseLogo";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import { PageQuery, toQueryParam } from "../../utils/models/pageQuery";
import { Organization } from "../../core/models/entities/Organization";
import { PagedResponse } from "../models/responses/pagedResponse";
import { mapToLogo } from "../models/mappers/mapLogo";
import { markLogoListAsStale } from "../../utils/functions/logo-refresh.util";
import { LogoOptions } from "../../core/models/entities/LogoOptions";
import { mapLogoOptions } from "../models/mappers/mapLogoOptions";
import { TransferOptions } from "../../core/models/entities/TransferOptions";
import { mapTransferOptions } from "../models/mappers/mapTransferOptions";
import {
  ChangeLogoColors,
  InitiateApproval,
} from "../../presentation/screens/logo-list/hooks/useLogoActionsHandler";
import { LogoCommit } from "../models/bodies/LogoCommit";
import { CreateMaskQueueCount } from "../models/responses/CreateMaskQueueCount";
import { Verdict } from "../../presentation/components/ApprovalVoting/approval-voting";
import { LogoStorageCountByOrgDto } from "../models/responses/LogoDto";

export interface LogoRepository {
  changeColors(
    orgId: Organization["id"],
    logoId: BaseLogo["id"],
    colors: ChangeLogoColors,
  ): Promise<BaseLogo>;
  changeTitle(
    orgId: Organization["id"],
    logoId: BaseLogo["id"],
    title: string,
  ): Promise<BaseLogo>;
  useLogo(orgId: number, logoId: number): Resource<BaseLogo>;
  useOptions(options?: { enabled?: boolean }): Resource<LogoOptions>;
  prefetchOptions(): Promise<void>;
  useTransferOptions(
    vendorId?: number,
    options?: { enabled?: boolean },
  ): Resource<TransferOptions>;
  prefetchTransferOptions(vendorId?: number): Promise<void>;
  useAllLogos(
    currentPageQuery?: PageQuery,
    options?: { keepPreviousData?: boolean },
  ): Resource<PagedResponse<BaseLogo>>;
  prefetchLogos(query: PageQuery): Promise<void>;
  useAllLogosForDesignInfinity(
    customerId: number,
    currentPageQuery?: PageQuery,
  ): UseInfiniteQueryResult<PagedResponse<BaseLogo>>;
  useAllLogosInfinity(
    query: PageQuery,
  ): UseInfiniteQueryResult<PagedResponse<BaseLogo>>;
  createLogo(logo: File, orgId: number): Promise<BaseLogo>;
  createTextLogo(
    value: string,
    color: string,
    heightMm: number,
    font: string,
    orgId: number,
    customerId?: number,
    method?: string,
  ): Promise<BaseLogo>;
  createNameLogo(
    color: string,
    heightMm: number,
    font: string,
    orgId: number,
    customerId?: number,
    method?: string,
  ): Promise<BaseLogo>;
  createNumberLogo(
    color: string,
    heightMm: number,
    font: string,
    method: string,
    orgId: number,
    customerId?: number,
  ): Promise<BaseLogo>;
  createBaseLogo(customerId: number, title: string): Promise<BaseLogo>;
  uploadFileToBaseLogo(
    logo: File,
    customerId: number,
    logoId: number,
  ): Promise<BaseLogo>;
  update(orgId: number, logo: BaseLogo): Promise<BaseLogo>;
  resize(orgId: number, logo: BaseLogo): Promise<BaseLogo>;
  copy(orgId: number, logoId: number): Promise<BaseLogo>;
  commit(logo: BaseLogo, commit: LogoCommit): Promise<BaseLogo>;
  initiateApproval(
    logo: BaseLogo,
    approval: InitiateApproval,
  ): Promise<BaseLogo>;
  sendApproval(logo: BaseLogo, email: string): Promise<void>;
  sendApprovalConditional(logo: BaseLogo, email: string): Promise<void>;
  placeVerdict(logo: BaseLogo, verdict: Verdict): Promise<BaseLogo>;
  delete(orgId: number, logoId: number): Promise<void>;
  generateTextLogoPreview: (
    value: string,
    color: string,
    heightMM: number,
    font: string,
    orgId: number,
  ) => Promise<string>;
  generateNumberLogoPreview: (
    color: string,
    heightMm: number,
    font: string,
    orgId: number,
  ) => Promise<string>;
  refreshStorageCount(logoId: BaseLogo["id"]): Promise<void>;
  getLogoStorageCounts(
    logoId: BaseLogo["id"],
  ): Promise<LogoStorageCountByOrgDto[]>;
  refreshLogoStorageCountForOrg(
    logoId: BaseLogo["id"],
    organizationId: number,
  ): Promise<void>;
  getImportLogosQueueCounts(): Promise<CreateMaskQueueCount[]>;
  useImportLogosQueueCounts(): Resource<CreateMaskQueueCount[]>;
  getImportPricesQueueCounts(): Promise<CreateMaskQueueCount[]>;
}

// The options catalogs (logo-options and transfer-options/GetTemplatesSizes)
// change rarely and are Redis-cached server-side. GetTemplatesSizes is also a
// heavy NAV call (p50 ~0.8s, p95 ~6s), so a long staleTime lets re-opening the
// Commission/Pricing sections (and idle/hover prefetches) hit the cache
// instead of showing a spinner.
const OPTIONS_CATALOG_STALE_TIME = 30 * 60 * 1000; // 30 min

export function useLogoRepository(): LogoRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  async function invalidateQueries() {
    await queryClient.invalidateQueries({
      queryKey: ["logos"],
      type: "all",
    });
  }

  function useOptions(options?: { enabled?: boolean }): Resource<LogoOptions> {
    return useQuery(
      ["logo-options"],
      () => apiClient.getLogoOptions().then(mapLogoOptions),
      {
        staleTime: OPTIONS_CATALOG_STALE_TIME,
        enabled: options?.enabled ?? true,
      },
    );
  }

  // Warm the logo-options cache (same key/queryFn/staleTime as useOptions) so
  // screens that gate on it are served from cache. Fail-silent: a miss just
  // means useOptions fetches as usual.
  function prefetchOptions(): Promise<void> {
    return queryClient.prefetchQuery(
      ["logo-options"],
      () => apiClient.getLogoOptions().then(mapLogoOptions),
      { staleTime: OPTIONS_CATALOG_STALE_TIME },
    );
  }

  // The key includes vendorId: the response is vendor-specific (NAV prices/
  // sizes per vendor), so with a long staleTime a shared key would serve one
  // vendor's catalog for another (e.g. an admin editing customers of two
  // different vendors).
  function useTransferOptions(
    vendorId?: number,
    options?: { enabled?: boolean },
  ): Resource<TransferOptions> {
    return useQuery(
      ["transfer-options", vendorId],
      () => apiClient.getTransferOptions(vendorId).then(mapTransferOptions),
      {
        staleTime: OPTIONS_CATALOG_STALE_TIME,
        enabled: options?.enabled ?? true,
      },
    );
  }

  // Warm the transfer-options cache (same key/queryFn/staleTime as
  // useTransferOptions) so the Commission & VAT / Pricing sections open
  // instantly instead of spinning on the heavy GetTemplatesSizes NAV call.
  // Fail-silent: prefetchQuery swallows errors, and a miss just means the
  // section's own lazy hook fetches as usual.
  function prefetchTransferOptions(vendorId?: number): Promise<void> {
    return queryClient.prefetchQuery(
      ["transfer-options", vendorId],
      () => apiClient.getTransferOptions(vendorId).then(mapTransferOptions),
      { staleTime: OPTIONS_CATALOG_STALE_TIME },
    );
  }

  function useLogo(orgId: number, logoId: number): Resource<BaseLogo> {
    return useQuery(
      ["logos", logoId],
      () => apiClient.getLogo(orgId, logoId).then(mapToLogo),
      {
        // Short freshness window so a detail query mounted right after a
        // seedAndMarkStale (clone flow: copy seeds the cache, the editor mounts
        // <1s later) doesn't immediately re-fetch the response it was just
        // seeded with - each GetLogo costs ~1.4-1.8s server-side (SOAP+NAV) -
        // and doesn't overwrite the optimistic dims seed while the background
        // size restore is still in flight. Explicit invalidations (color
        // change, title, assign customer, ...) still refetch active queries
        // regardless of staleTime, so edit flows are unaffected.
        staleTime: 15_000,
      },
    );
  }

  function useAllLogos(
    query: PageQuery,
    options?: { keepPreviousData?: boolean },
  ): Resource<PagedResponse<BaseLogo>> {
    // If a logo type was filtered, extend the route by a type parameter.
    const typeFilter = query.filterBy.find((it) => it.field === "logoType");
    const queryCopy = { ...query };
    queryCopy.filterBy = queryCopy.filterBy.filter(
      (it) => it.field !== "logoType",
    );
    return useQuery(
      ["logos", typeFilter?.value, toQueryParam(query)],
      ({ signal }) =>
        apiClient.getAllLogos(queryCopy, typeFilter?.value, signal).then(
          // Null-guard: a throw in this transform rejects the query and shows
          // the error view even on a 200 — degrade to an empty page instead.
          (data) => ({
            ...data,
            results: data.results ? data.results.map(mapToLogo) : [],
          }),
        ),
      {
        staleTime: 60000, // 1 min
        refetchOnWindowFocus: false,
        // keepPreviousData is OPT-IN (default off): with it, isLoading stays
        // false on query changes, which changes what consumers observe. The
        // list screens opt in and dim the previous rows via
        // isFetching/isPreviousData instead of collapsing into a spinner.
        // Other consumers (commission-editor's SKU lookup, admin import
        // screens, design editor) rely on isLoading gating and keep the
        // previous behavior. First load still shows the normal spinner.
        keepPreviousData: options?.keepPreviousData ?? false,
      },
    );
  }

  // Prefetch a page into the same cache useAllLogos reads. It only warms the
  // cache (never changes what the main query renders), and it respects the
  // same 60s staleTime so prices stay as fresh as a normal click.
  // Returns the (never-rejecting) prefetch promise so callers can track in-flight work.
  function prefetchLogos(query: PageQuery): Promise<void> {
    const typeFilter = query.filterBy.find((it) => it.field === "logoType");
    const queryCopy = { ...query };
    queryCopy.filterBy = queryCopy.filterBy.filter(
      (it) => it.field !== "logoType",
    );
    return queryClient.prefetchQuery(
      ["logos", typeFilter?.value, toQueryParam(query)],
      () =>
        apiClient.getAllLogos(queryCopy, typeFilter?.value).then((data) => ({
          ...data,
          results: data.results ? data.results.map(mapToLogo) : [],
        })),
      { staleTime: 60000 },
    );
  }

  function useAllLogosForDesignInfinity(
    customerId: number,
    query: PageQuery,
  ): UseInfiniteQueryResult<PagedResponse<BaseLogo>> {
    return useInfiniteQuery({
      queryKey: ["logos", customerId, "inf", toQueryParam(query)],
      queryFn: ({ pageParam }) => {
        const copy: PageQuery = {
          page: {
            index: pageParam ?? query.page.index,
            size: query.page.size,
          },
          filterBy: query.filterBy,
          sortBy: {
            field: query.sortBy.field,
            sortDirection: query.sortBy.sortDirection,
          },
          searchTerm: query.searchTerm,
        };

        return apiClient.getAllLogosForDesign(customerId, copy);
      },
      // Null-guards: a degraded page must not throw here — that puts the
      // editor's logo picker in an error state on a 200 response.
      getNextPageParam: (lastPage) => {
        if (!lastPage || lastPage.pageIndex === lastPage.pageCount) {
          return undefined;
        }
        return lastPage.pageIndex + 1;
      },
      getPreviousPageParam: (firstPage) => {
        if (!firstPage || firstPage.pageIndex === 1) {
          return undefined;
        }

        return firstPage.pageIndex - 1;
      },
    });
  }

  function useAllLogosInfinity(
    query: PageQuery,
  ): UseInfiniteQueryResult<PagedResponse<BaseLogo>> {
    return useInfiniteQuery({
      queryKey: ["logos", "inf", toQueryParam(query)],
      queryFn: ({ pageParam }) => {
        const copy: PageQuery = {
          page: {
            index: pageParam ?? query.page.index,
            size: query.page.size,
          },
          filterBy: query.filterBy,
          sortBy: {
            field: query.sortBy.field,
            sortDirection: query.sortBy.sortDirection,
          },
          searchTerm: query.searchTerm,
        };

        return apiClient.getAllLogos(copy);
      },
      getNextPageParam: (lastPage) => {
        if (lastPage.pageIndex === lastPage.pageCount) {
          return undefined;
        }
        return lastPage.pageIndex + 1;
      },
      getPreviousPageParam: (firstPage) => {
        if (firstPage.pageIndex === 1) {
          return undefined;
        }

        return firstPage.pageIndex - 1;
      },
    });
  }

  function deleteLogo(orgId: number, logoId: number): Promise<void> {
    // Optimistically remove from cache immediately
    queryClient.setQueriesData<PagedResponse<BaseLogo>>(
      ["logos"],
      (oldData) => {
        if (!oldData || !oldData.results) return oldData;

        return {
          ...oldData,
          results: oldData.results.filter((logo) => logo.id !== logoId),
        };
      },
    );

    // Remove individual logo cache entry
    queryClient.removeQueries({ queryKey: ["logos", logoId] });

    // Update infinite queries - use predicate function to match infinite queries
    queryClient.setQueriesData(["logos"], (oldData: any) => {
      if (!oldData || !oldData.pages) return oldData;

      return {
        ...oldData,
        pages: oldData.pages.map((page: PagedResponse<BaseLogo>) => ({
          ...page,
          results: page.results
            ? page.results.filter((logo: BaseLogo) => logo.id !== logoId)
            : [],
        })),
      };
    });

    return apiClient.deleteLogoById(orgId, logoId).catch((error) => {
      // If deletion fails, invalidate queries to restore correct state
      invalidateQueries();
      throw error;
    });
  }

  async function createImageLogo(
    logoFile: File,
    orgId: number,
  ): Promise<BaseLogo> {
    let lr = await apiClient.createAndUploadImageLogo(logoFile, orgId);
    await invalidateQueries();
    return mapToLogo(lr);
  }

  async function createTextLogo(
    value: string,
    color: string,
    heightMM: number,
    font: string,
    orgId: number,
    customerId?: number,
    method?: string,
  ): Promise<BaseLogo> {
    let lr = await apiClient.createTextLogo(orgId, {
      color: color,
      heightMm: heightMM,
      value: value,
      font: font,
      customerId: customerId,
      method: method,
    });
    await invalidateQueries();
    return mapToLogo(lr);
  }

  async function createNameLogo(
    color: string,
    heightMM: number,
    font: string,
    orgId: number,
    customerId?: number,
    method?: string,
  ): Promise<BaseLogo> {
    let lr = await apiClient.createPlaceholderNameLogo(orgId, {
      color: color,
      heightMm: heightMM,
      font: font,
      method: method,
      customerId: customerId,
    });
    await invalidateQueries();
    return mapToLogo(lr);
  }

  async function createNumberLogo(
    color: string,
    heightMM: number,
    font: string,
    method: string,
    orgId: number,
    customerId?: number,
  ): Promise<BaseLogo> {
    let lr = await apiClient.createPlaceholderNumberLogo(orgId, {
      color: color,
      heightMm: heightMM,
      font: font,
      method: method,
      customerId: customerId,
    });
    await invalidateQueries();
    return mapToLogo(lr);
  }

  async function createBaseLogo(
    organizationId: number,
    title: string,
  ): Promise<BaseLogo> {
    // TODO Fix own logged in user's id.
    let lr = await apiClient.createImageLogo({
      title,
      customerId: organizationId,
    });
    return mapToLogo(lr);
  }

  async function uploadFileToBaseLogo(
    logo: File,
    customerId: number,
    logoId: number,
  ): Promise<BaseLogo> {
    let lr = await apiClient.uploadFileImageLogo(logo, customerId, logoId);
    return mapToLogo(lr);
  }

  async function changeColors(
    orgId: Organization["id"],
    logoId: BaseLogo["id"],
    colors: ChangeLogoColors,
  ): Promise<BaseLogo> {
    // console.log("changeColors", colors);

    const lrDto = await apiClient.changeColors(orgId, logoId, colors);
    const lr = mapToLogo(lrDto as any);

    queryClient.setQueryData(["logos", logoId], lr);
    queryClient.setQueriesData<PagedResponse<BaseLogo>>(
      ["logos"],
      (oldData) => {
        if (!oldData || !oldData.results) return oldData;
        return {
          ...oldData,
          results: oldData.results.map((item) =>
            item.id === logoId ? lr : item,
          ),
        };
      },
    );

    queryClient.setQueriesData(["logos"], (oldData: any) => {
      if (!oldData || !oldData.pages) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page: PagedResponse<BaseLogo>) => ({
          ...page,
          results: page.results
            ? page.results.map((item: BaseLogo) =>
                item.id === logoId ? lr : item,
              )
            : [],
        })),
      };
    });

    return lr;
  }

  async function changeTitle(
    orgId: Organization["id"],
    logoId: number,
    title: string,
  ): Promise<BaseLogo> {
    const lr = await apiClient.changeLogoTitle(orgId, logoId, title);
    await invalidateQueries();
    return lr;
  }

  async function update(orgId: number, logo: BaseLogo): Promise<BaseLogo> {
    const lr = await apiClient.updateLogo(orgId, logo);
    return lr;
  }

  // Copy/resize used to AWAIT a full ["logos"] invalidation (refetching every
  // active AND inactive list query) before returning - twice per clone - which
  // held the click->editor path hostage for seconds. Instead: seed the detail
  // cache with the mapped response (the editor then renders with zero extra
  // GET, low-res preview included as a data: URI) and only mark list queries
  // stale; the list refetches when the user actually returns to it.
  function seedAndMarkStale(lrDto: BaseLogo): BaseLogo {
    const lr = mapToLogo(lrDto as any);
    queryClient.setQueryData(["logos", lr.id], lr);
    queryClient.invalidateQueries({
      queryKey: ["logos"],
      refetchType: "none",
      predicate: (query) =>
        !(query.queryKey.length === 2 && typeof query.queryKey[1] === "number"),
    });
    markLogoListAsStale();
    return lr;
  }

  async function resize(orgId: number, logo: BaseLogo): Promise<BaseLogo> {
    const lr = await apiClient.resizeLogo(orgId, logo);
    return seedAndMarkStale(lr);
  }

  async function copy(orgId: number, logoId: number): Promise<BaseLogo> {
    const lr = await apiClient.copyLogo(orgId, logoId);
    return seedAndMarkStale(lr);
  }

  async function commit(
    logo: BaseLogo,
    logoCommit: LogoCommit,
  ): Promise<BaseLogo> {
    const lr = await apiClient.commitLogo(logo, logoCommit);
    await invalidateQueries();
    return lr;
  }

  async function placeVerdict(
    logo: BaseLogo,
    verdict: Verdict,
  ): Promise<BaseLogo> {
    const lrDto = await apiClient.placeVerdictLogo(logo, verdict);
    const lr = mapToLogo(lrDto as any);
    queryClient.setQueryData(["logos", lr.id], lr);
    queryClient.setQueriesData<PagedResponse<BaseLogo>>(
      ["logos"],
      (oldData) => {
        if (!oldData || !oldData.results) return oldData;
        return {
          ...oldData,
          results: oldData.results.map((item) =>
            item.id === lr.id ? lr : item,
          ),
        };
      },
    );
    queryClient.setQueriesData(["logos"], (oldData: any) => {
      if (!oldData || !oldData.pages) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page: PagedResponse<BaseLogo>) => ({
          ...page,
          results: page.results
            ? page.results.map((item: BaseLogo) =>
                item.id === lr.id ? lr : item,
              )
            : [],
        })),
      };
    });
    return lr;
  }

  async function initiateApproval(
    logo: BaseLogo,
    approval: InitiateApproval,
  ): Promise<BaseLogo> {
    const lrDto = await apiClient.initiateApprovalLogo(logo, approval);
    const lr = mapToLogo(lrDto as any);

    queryClient.setQueryData(["logos", lr.id], lr);
    await queryClient.invalidateQueries({
      queryKey: ["logos"],
      predicate: (query) => {
        const key = query.queryKey;
        // Invalidate all 'logos' queries EXCEPT the specific detail query for a logo ID
        // Detail query format: ["logos", number]
        // List query format: ["logos", string, string] or ["logos", number, "inf", string]
        return !(key.length === 2 && typeof key[1] === "number");
      },
    });

    return lr;
  }

  async function sendApproval(logo: BaseLogo, email: string): Promise<void> {
    const lr = await apiClient.sendApprovalLogo(logo, email);
  }

  async function sendApprovalConditional(
    logo: BaseLogo,
    email: string,
  ): Promise<void> {
    const lr = await apiClient.sendApprovalLogoConditional(logo, email);
  }

  async function generateTextLogoPreview(
    value: string,
    color: string,
    heightMM: number,
    font: string,
    orgId: number,
  ): Promise<string> {
    return await apiClient.generateTextLogoPreview(orgId, {
      color: color,
      heightMm: heightMM,
      value: value,
      font: font,
    });
  }

  async function generateNumberLogoPreview(
    color: string,
    heightMm: number,
    font: string,
    orgId: number,
  ): Promise<string> {
    return await apiClient.generateNumberLogoPreview(orgId, {
      color: color,
      heightMm: heightMm,
      font: font,
    });
  }

  async function refreshStorageCount(logoId: BaseLogo["id"]): Promise<void> {
    await apiClient.refreshStorageCount(logoId);
    await invalidateQueries();
  }

  async function getLogoStorageCounts(
    logoId: BaseLogo["id"],
  ): Promise<LogoStorageCountByOrgDto[]> {
    return await apiClient.getLogoStorageCounts(logoId);
  }

  async function refreshLogoStorageCountForOrg(
    logoId: BaseLogo["id"],
    organizationId: number,
  ): Promise<void> {
    await apiClient.refreshLogoStorageCountForOrg(logoId, organizationId);
    await invalidateQueries();
  }

  async function getImportLogosQueueCounts(): Promise<CreateMaskQueueCount[]> {
    return await apiClient.getImportLogosQueueCounts();
  }

  // React Query version for the header queue badges: dedups the header's repeated
  // remounts into one request (the imperative call used to fire ~5x per page load)
  // while keeping the 60s polling via refetchInterval.
  function useImportLogosQueueCounts(): Resource<CreateMaskQueueCount[]> {
    return useQuery(
      ["import-queue-counts", "logos"],
      () => apiClient.getImportLogosQueueCounts(),
      {
        refetchInterval: 60000,
        staleTime: 55000,
      },
    );
  }

  async function getImportPricesQueueCounts(): Promise<CreateMaskQueueCount[]> {
    return await apiClient.getImportPricesQueueCounts();
  }

  return {
    useOptions,
    prefetchOptions,
    useTransferOptions,
    prefetchTransferOptions,
    useLogo,
    useAllLogos,
    prefetchLogos,
    useAllLogosForDesignInfinity,
    delete: deleteLogo,
    createLogo: createImageLogo,
    createTextLogo,
    createNameLogo,
    createNumberLogo,
    createBaseLogo,
    uploadFileToBaseLogo,
    changeColors,
    changeTitle,
    update,
    resize,
    copy,
    commit,
    useAllLogosInfinity,
    initiateApproval,
    sendApproval,
    sendApprovalConditional,
    placeVerdict,
    generateTextLogoPreview,
    generateNumberLogoPreview,
    refreshStorageCount,
    getLogoStorageCounts,
    refreshLogoStorageCountForOrg,
    getImportLogosQueueCounts,
    useImportLogosQueueCounts,
    getImportPricesQueueCounts,
  };
}
